// import lineClearSound from '../sounds/lineclear.wav'; // REMOVE this assumption
import blip1Sound from '../../core/engine/sounds/blip1.wav'; // Import the correct flash sound
import depose1Sound from '../../core/engine/sounds/metal1.wav'; // Assuming this filename
import depose2Sound from '../../core/engine/sounds/metal3.wav'; // Assuming this filename
import depose3Sound from '../../core/engine/sounds/metal6.wav'; // Assuming this filename
import levelupSound from '../../core/engine/sounds/glissup.wav'; // Level up sound
import { AudioPlaybackHandle, IAudioManager } from '../../core/engine/types/audio-manager';

export default class AudioManager implements IAudioManager {
    private sounds: Map<string, HTMLAudioElement>;
    private soundPaths: Map<string, string>; // Store paths for creating new instances
    private globalVolume: number = 1.0; // Overall volume control (0.0 to 1.0)
    private sfxVolume: number = 1.0;    // Sound effects volume control

    constructor() {
        this.sounds = new Map<string, HTMLAudioElement>();
        this.soundPaths = new Map<string, string>();
    }

    /**
     * Preloads all sounds required for the game.
     */
    preloadGameSounds(): void {
        // Load all sounds and ensure they're fully ready
        const soundsToLoad = [
            { name: 'lineFlash', path: blip1Sound },
            { name: 'depose1', path: depose1Sound },
            { name: 'depose2', path: depose2Sound },
            { name: 'depose3', path: depose3Sound },
            { name: 'levelup', path: levelupSound }
        ];
        
        soundsToLoad.forEach(({ name, path }) => {
            this.loadSound(name, path);
        });
    }

    /**
     * Loads a sound effect and stores it for later playback.
     * @param name - A unique name to identify the sound.
     * @param path - The path to the audio file.
     */
    loadSound(name: string, path: string): void {
        if (this.sounds.has(name)) {
            return;
        }
        try {
            const audio = new Audio(path);
            audio.preload = 'auto'; // Preload the audio
            
            // Force load and wait for it to be ready
            const loadPromise = new Promise<void>((resolve) => {
                const onCanPlayThrough = () => {
                    audio.removeEventListener('canplaythrough', onCanPlayThrough);
                    audio.removeEventListener('error', onError);
                    resolve();
                };
                const onError = () => {
                    audio.removeEventListener('canplaythrough', onCanPlayThrough);
                    audio.removeEventListener('error', onError);
                    console.error(`Error loading sound "${name}"`);
                    resolve(); // Still resolve to not block
                };
                audio.addEventListener('canplaythrough', onCanPlayThrough, { once: true });
                audio.addEventListener('error', onError, { once: true });
                audio.load(); // Start loading
            });
            
            // Store the audio and path
            this.sounds.set(name, audio);
            this.soundPaths.set(name, path);
            
            // Wait for load (but don't block)
            loadPromise.catch(() => {
                // Ignore errors, audio might still work
            });
        } catch (error) {
            console.error(`Error loading sound "${name}" from ${path}:`, error);
        }
    }

    /**
     * Plays a loaded sound effect.
     * @param name - The name of the sound to play.
     * @param playbackRate - Optional playback rate (1.0 is normal pitch).
     */
    playSound(name: string, playbackRate: number = 1.0): AudioPlaybackHandle | null {
        const preloadedAudio = this.sounds.get(name);
        if (!preloadedAudio) {
            console.warn(`Sound "${name}" not found.`);
            return null;
        }
        
        // Clamp playbackRate to valid range (HTML5 Audio supports 0.25 to 4.0)
        const clampedRate = Math.max(0.25, Math.min(4, playbackRate));
        
        // Clone the preloaded audio element for instant playback
        const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
        
        // Set properties immediately
        audio.volume = this.globalVolume * this.sfxVolume;
        audio.currentTime = 0;
        audio.playbackRate = clampedRate;
        
        // Debug logging
        if (name === 'lineFlash') {
            console.log(`[AudioManager] Playing ${name} with playbackRate=${clampedRate.toFixed(3)}`);
        }
        
        let stopped = false;

        const cleanup = () => {
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('canplay', onCanPlay);
        };

        const stopPlayback = () => {
            if (stopped) return;
            stopped = true;
            audio.pause();
            audio.currentTime = 0;
            cleanup();
        };

        function onEnded() {
            stopped = true;
            cleanup();
        }

        function onCanPlay() {
            audio.playbackRate = clampedRate;
            audio.play().catch(err => {
                console.error(`Error playing sound "${name}":`, err);
            });
        }

        audio.addEventListener('ended', onEnded, { once: true });
        
        // Try to play immediately - don't wait for readyState checks
        audio.play().catch(error => {
            // Only wait if play actually fails
            if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
                // Wait for audio to be ready
                audio.addEventListener('canplay', onCanPlay, { once: true });
            } else {
                // Other errors - might be a real problem
                console.error(`Error playing sound "${name}":`, error);
            }
        });

        return {
            stop: () => {
                stopPlayback();
            },
            audioElement: audio,
        };
    }

    /**
     * Sets the global volume level.
     * @param volume - Volume level from 0.0 (mute) to 1.0 (full).
     */
    setGlobalVolume(volume: number): void {
        this.globalVolume = Math.max(0, Math.min(1, volume));
    }

    /**
     * Sets the sound effects volume level.
     * @param volume - Volume level from 0.0 (mute) to 1.0 (full).
     */
    setSfxVolume(volume: number): void {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
    }

    // Potential future methods:
    // playMusic(name: string), stopMusic(), setMusicVolume(), stopSound(name), stopAllSounds(), mute(), unmute()
} 